import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';

import useI18n from '../../hooks/useI18n';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent } from '../Dialog';
import type {
  CommandProps,
  CommandDialogProps,
  CommandInputProps,
  CommandListProps,
  CommandEmptyProps,
  CommandGroupProps,
  CommandSeparatorProps,
  CommandItemProps,
  CommandShortcutProps,
} from './types';

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const Command = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive>,
  CommandProps
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'eye:text-popover-foreground eye:flex eye:h-full eye:w-full eye:flex-col eye:overflow-hidden eye:bg-white',
      className,
    )}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className='eye:overflow-hidden eye:p-0'>
        <Command className='eye:[&_[cmdk-group-heading]]:px-2 eye:[&_[cmdk-group-heading]]:text-muted-foreground eye:[&_[cmdk-group]]:px-2 eye:[&_[cmdk-input-wrapper]_svg]:h-5 eye:[&_[cmdk-input-wrapper]_svg]:w-5 eye:[&_[cmdk-item]]:px-2 eye:[&_[cmdk-item]]:py-3 eye:[&_[cmdk-item]_svg]:h-5 eye:[&_[cmdk-item]_svg]:w-5 eye:[&_[cmdk-group-heading]]:font-medium eye:[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 eye:[&_[cmdk-input]]:h-12'>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandInput = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Input>,
  CommandInputProps
>(({ className, ...props }, ref) => (
  <div
    className='eye:flex eye:items-center eye:border-b eye:border-grey-20 eye:px-20 eye:pb-4xs'
    cmdk-input-wrapper=''
  >
    <Search className='eye:mx-4' />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'eye:text-sm eye:flex eye:w-full eye:outline-none eye:placeholder:text-grey-placeholder' +
          ' eye:disabled:cursor-not-allowed eye:disabled:text-grey-disabled',
        className,
      )}
      {...props}
    />
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandList = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.List>,
  CommandListProps
>(({ className, label, ...props }, ref) => {
  const { t } = useI18n();

  return (
    <CommandPrimitive.List
      ref={ref}
      // cmdk names the listbox "Suggestions" in English unless given a label.
      label={label ?? t('suggestions')}
      className={cn(
        'eye:max-h-[300px] eye:overflow-x-hidden eye:overflow-y-auto',
        className,
      )}
      {...props}
    />
  );
});

CommandList.displayName = CommandPrimitive.List.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandEmpty = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Empty>,
  CommandEmptyProps
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className='eye:text-sm eye:px-[26px] eye:pt-4xs'
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandGroup = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Group>,
  CommandGroupProps
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'eye:text-foreground eye:[&_[cmdk-group-heading]]:px-2 eye:[&_[cmdk-group-heading]]:py-1.5 eye:[&_[cmdk-group-heading]]:text-xs eye:[&_[cmdk-group-heading]]:text-muted-foreground eye:overflow-hidden eye:[&_[cmdk-group-heading]]:font-medium',
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandSeparator = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Separator>,
  CommandSeparatorProps
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('eye:bg-border', className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandItem = React.forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Item>,
  CommandItemProps
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'eye:body-m eye:flex eye:min-h-48 eye:w-full eye:cursor-pointer eye:text-black',
      'eye:items-center eye:px-[26px] eye:py-5xs eye:select-none',
      'eye:outline-none eye:select-none',
      'data-[disabled=true]:eye:pointer-events-none data-[disabled=true]:eye:opacity-50',
      'eye:[&_svg]:pointer-events-none eye:[&_svg]:shrink-0',
      className,
      'eye:hover:bg-grey-10',
      'eye:data-[selected=true]:bg-grey-10',
      'eye:aria-selected:bg-grey-10',
      // Shape, not colour alone: the tinted background is far below 3:1. cmdk
      // marks the highlighted item with data-selected, not data-highlighted.
      'eye:data-[selected=true]:inset-ring-2 eye:data-[selected=true]:inset-ring-black',
      'eye:text-wrap',
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

/**
 * @deprecated Used for the old, deprecated Command component. Do not implement anywhere else.
 */
const CommandShortcut = ({ className, ...props }: CommandShortcutProps) => {
  return (
    <span
      className={cn(
        'eye:text-xs eye:tracking-widest eye:text-muted-foreground eye:ml-auto',
        className,
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
